// Generated by OrchestKit Claude Plugin
// Created: 2026-09-09

/**
 * Client HTTP de l'API — doc 06.
 *
 * Deux décisions structurantes.
 *
 * **Le jeton d'accès vit en MÉMOIRE, jamais dans `localStorage`.** Un jeton
 * stocké est lisible par n'importe quel script injecté dans la page. Perdre
 * la session à chaque rechargement d'onglet est le prix de cette garantie ; le
 * jeton de rafraîchissement, lui, est révocable côté serveur (RG-167), donc
 * son vol se répare.
 *
 * **Le rafraîchissement est SÉRIALISÉ.** Le jeton d'accès dure 15 minutes.
 * Quand il expire, plusieurs requêtes concurrentes se voient refuser en même
 * temps ; sans verrou, chacune tenterait son propre renouvellement et les
 * jetons à usage unique s'invalideraient les uns les autres. Un seul
 * renouvellement est lancé, les autres l'attendent.
 */

/**
 * Racine de l'API.
 *
 * Relatif par défaut — développement, et déploiement en origine unique, où
 * le serveur web relaie `/api`. Absolu quand l'API vit sur un autre domaine,
 * via `VITE_URL_API` posée à la compilation. Dans ce second cas, l'API doit
 * déclarer cette origine dans `ORIGINES_AUTORISEES`, faute de quoi le
 * navigateur refusera chaque requête.
 */
const BASE = import.meta.env.VITE_URL_API ?? '/api';

export class ErreurApi extends Error {
  readonly statut: number;
  readonly code: string;
  readonly regle: string | undefined;
  readonly details: Record<string, unknown> | undefined;

  constructor(
    statut: number,
    code: string,
    message: string,
    regle?: string,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ErreurApi';
    this.statut = statut;
    this.code = code;
    this.regle = regle;
    this.details = details;
  }
}

export interface Session {
  jetonAcces: string;
  jetonRafraichissement: string;
  utilisateur: {
    id: string;
    nom: string;
    prenom: string | null;
    pinAChanger: boolean;
  };
  role: string;
  permissions: string[];
  /** Identifiants seulement : les noms se lisent via `GET /boutiques`. */
  boutiques: string[];
}

let session: Session | null = null;
let renouvellementEnCours: Promise<boolean> | null = null;
const abonnes = new Set<(session: Session | null) => void>();

export function sessionCourante(): Session | null {
  return session;
}

export function definirSession(nouvelle: Session | null): void {
  session = nouvelle;
  for (const abonne of abonnes) {
    abonne(nouvelle);
  }
}

export function surChangementSession(
  abonne: (session: Session | null) => void,
): () => void {
  abonnes.add(abonne);
  return () => abonnes.delete(abonne);
}

export function aPermission(code: string): boolean {
  return session?.permissions.includes(code) ?? false;
}

interface CorpsErreur {
  erreur?: {
    code?: string;
    message?: string;
    regle?: string;
    details?: Record<string, unknown>;
  };
}

async function versErreur(reponse: Response): Promise<ErreurApi> {
  let corps: CorpsErreur = {};
  try {
    corps = (await reponse.json()) as CorpsErreur;
  } catch {
    // Réponse sans JSON — un 502 du proxy, typiquement.
  }

  return new ErreurApi(
    reponse.status,
    corps.erreur?.code ?? 'ERREUR_RESEAU',
    corps.erreur?.message ?? `Le serveur a répondu ${String(reponse.status)}.`,
    corps.erreur?.regle,
    corps.erreur?.details,
  );
}

/**
 * Renouvelle le jeton d'accès, une seule fois à la fois.
 *
 * EXPORTÉ parce que la portée d'un utilisateur — la liste de ses boutiques —
 * est gravée dans le jeton, et figée pour toute sa durée de vie. Après avoir
 * créé une boutique, la session en cours ne la voit donc pas : elle existe en
 * base, elle est rattachée à son créateur, et le jeton l'ignore encore.
 *
 * Renouveler force le serveur à frapper un jeton à partir de l'état RÉEL de
 * la base. C'est le seul moyen de voir sa nouvelle boutique sans se
 * déconnecter.
 *
 * @returns `true` si la session continue, `false` si elle est perdue.
 */
export async function renouvelerSession(): Promise<boolean> {
  return renouveler();
}

async function renouveler(): Promise<boolean> {
  if (renouvellementEnCours !== null) {
    return renouvellementEnCours;
  }

  const courante = session;
  if (courante === null) {
    return false;
  }

  renouvellementEnCours = (async () => {
    try {
      const reponse = await fetch(`${BASE}/auth/rafraichir`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          jeton_rafraichissement: courante.jetonRafraichissement,
        }),
      });

      if (!reponse.ok) {
        definirSession(null);
        return false;
      }

      const corps = (await reponse.json()) as {
        jeton_acces: string;
        jeton_rafraichissement: string;
      };

      definirSession({
        ...courante,
        jetonAcces: corps.jeton_acces,
        // Le jeton de rafraîchissement est à USAGE UNIQUE : garder l'ancien
        // condamnerait le renouvellement suivant.
        jetonRafraichissement: corps.jeton_rafraichissement,
      });
      return true;
    } catch {
      definirSession(null);
      return false;
    } finally {
      renouvellementEnCours = null;
    }
  })();

  return renouvellementEnCours;
}

export interface Options {
  methode?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Corps `multipart` (envoi de fichier). Exclusif de `corps`. */
  formulaire?: FormData;
  corps?: unknown;
  /** Obligatoire sur toute écriture — RG-008. */
  cleIdempotence?: string;
  /** Interne : empêche de boucler sur le renouvellement. */
  dejaRenouvele?: boolean;
}

/**
 * L'UNIQUE point d'accès HTTP à l'API.
 *
 * Adresse de base, jeton et renouvellement sur 401 vivent ici et nulle part
 * ailleurs. Les photos passaient auparavant par des `fetch` écrits à la main
 * vers `/api/...` en dur : ils ignoraient `VITE_URL_API`, donc partaient vers
 * le serveur du back-office dès que l'API vit sur un autre domaine — d'où un
 * refus sans message, la réponse n'étant pas celle de l'API. Et ils ne
 * renouvelaient pas le jeton expiré.
 */
async function executer(chemin: string, options: Options): Promise<Response> {
  const entetes: Record<string, string> = {};

  // Un corps `multipart` ne doit PAS porter de content-type fixé à la main :
  // le navigateur y écrit lui-même la frontière entre les parties.
  if (options.corps !== undefined && options.formulaire === undefined) {
    entetes['content-type'] = 'application/json';
  }
  if (session !== null) {
    entetes['authorization'] = `Bearer ${session.jetonAcces}`;
  }
  if (options.cleIdempotence !== undefined) {
    entetes['idempotency-key'] = options.cleIdempotence;
  }

  const corps =
    options.formulaire ??
    (options.corps === undefined ? undefined : JSON.stringify(options.corps));

  const reponse = await fetch(`${BASE}${chemin}`, {
    method: options.methode ?? 'GET',
    headers: entetes,
    ...(corps === undefined ? {} : { body: corps }),
  });

  /**
   * 401 : le jeton a expiré. On renouvelle et on rejoue UNE fois.
   *
   * Le drapeau évite la boucle : si la requête rejouée échoue encore, c'est
   * que la session est réellement perdue, et l'insister ne ferait qu'ajouter
   * des allers-retours à un utilisateur déjà déconnecté.
   */
  if (reponse.status === 401 && !options.dejaRenouvele && session !== null) {
    if (await renouveler()) {
      return executer(chemin, { ...options, dejaRenouvele: true });
    }
  }

  if (!reponse.ok) {
    throw await versErreur(reponse);
  }
  return reponse;
}

export async function appeler<T>(chemin: string, options: Options = {}): Promise<T> {
  const reponse = await executer(chemin, options);
  if (reponse.status === 204) {
    return undefined as T;
  }
  return (await reponse.json()) as T;
}

/**
 * Télécharge un fichier protégé — une photo, typiquement.
 *
 * Une balise `<img src="/api/...">` ne transmet AUCUN en-tête : l'API répond
 * 401 et l'image reste vide. Le fichier est donc lu ici, avec le jeton, puis
 * confié à l'image sous forme d'URL locale.
 */
export async function telecharger(chemin: string): Promise<Blob> {
  const reponse = await executer(chemin, {});
  return reponse.blob();
}

/**
 * Six chiffres exactement : un PIN de vendeur. Tout le reste : un mot de passe.
 *
 * Aucune ambiguïté possible : un mot de passe fait au moins 12 caractères.
 * L'administrateur, lui, ne peut se connecter QUE par mot de passe — le
 * serveur refuse son PIN, quel que soit l'écran.
 */
function secretDeConnexion(secret: string): { pin: string } | { mot_de_passe: string } {
  return /^\d{6}$/.test(secret) ? { pin: secret } : { mot_de_passe: secret };
}

export async function connecter(
  identifiant: string,
  secret: string,
): Promise<Session> {
  const reponse = await fetch(`${BASE}/auth/connexion`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ identifiant, ...secretDeConnexion(secret) }),
  });

  if (!reponse.ok) {
    throw await versErreur(reponse);
  }

  const corps = (await reponse.json()) as {
    jeton_acces: string;
    jeton_rafraichissement: string;
    utilisateur: { id: string; nom: string; prenom: string | null; pin_a_changer: boolean };
    role: string;
    permissions: string[];
    boutiques: string[];
  };

  const nouvelle: Session = {
    jetonAcces: corps.jeton_acces,
    jetonRafraichissement: corps.jeton_rafraichissement,
    utilisateur: {
      id: corps.utilisateur.id,
      nom: corps.utilisateur.nom,
      prenom: corps.utilisateur.prenom,
      pinAChanger: corps.utilisateur.pin_a_changer,
    },
    role: corps.role,
    permissions: corps.permissions,
    boutiques: corps.boutiques,
  };

  definirSession(nouvelle);
  return nouvelle;
}

export async function deconnecter(): Promise<void> {
  try {
    await appeler('/auth/deconnexion', { methode: 'POST' });
  } catch {
    // Une déconnexion qui échoue côté serveur ne doit pas retenir
    // l'utilisateur : on efface la session locale quoi qu'il arrive.
  }
  definirSession(null);
}

/** Clé d'idempotence pour une écriture — RG-008. */
export function cleNeuve(): string {
  return crypto.randomUUID();
}
